import { Form, Input, Skeleton, Space, Typography } from 'antd';

import {
  ConfirmDeletingElement,
  EditPageButtons,
  Error403Unauthorized,
} from '@app/components';
import { OFF, ONE_TIME_CODE } from '@app/constants';
import {
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_CATEGORY_CREATE,
  RIGHTS_IS_CATEGORY_EDIT,
  RIGHTS_IS_ON,
  RIGHTS_IS_SERVICE_CATEGORIES,
} from '@app/constants/accessRights';
import { ERROR, ERROR_REQUIRED_FIELD } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { useAccessRights } from '@app/hooks';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import { useAddServiceCategory } from '@pages/Clients/AddServiceCategory/hooks/useAddServiceCategory';

import type { FC } from 'react';

import '@pages/Clients/AddServiceCategory/styles/AddServiceCategory.desktop.scss';
import '@pages/Clients/AddServiceCategory/styles/AddServiceCategory.tablet.scss';

export const AddServiceCategory: FC = () => {
  const {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editableCategoryId,
    modalOpen,
    setModalOpen,
    handleCancel,
    deleteCategory,
    setInputData,
    initialValues,
  } = useAddServiceCategory();
  const { hasUserRightForModuleView } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_ON
  );
  const { hasUserRight: isRightToCategories } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_SERVICE_CATEGORIES
  );
  const { hasUserRight: isRightToCreate } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CATEGORY_CREATE
  );
  const { hasUserRight: isRightToEdit } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CATEGORY_EDIT
  );

  const isCreatingCategory = !editableCategoryId;
  const isEditingCategory = Boolean(editableCategoryId);

  const showError403Unauthorized =
    !hasUserRightForModuleView ||
    !isRightToCategories ||
    (isCreatingCategory && !isRightToCreate) ||
    (isEditingCategory && !isRightToEdit);

  if (showError403Unauthorized) {
    return <Error403Unauthorized />;
  }

  if (isLoading && isEditingCategory) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}add-service-category`}>
      <Form
        form={form}
        onFinish={submitForm}
        className="form"
        autoComplete={OFF}
        initialValues={initialValues}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="category-name"
            >
              Название
            </Typography.Text>

            <Form.Item
              name="Name"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
              ]}
            >
              <Input
                data-testid="category-name-input"
                autoComplete={ONE_TIME_CODE}
                maxLength={100}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Name',
                    isEditPage: Boolean(editableCategoryId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="category-description"
            >
              Описание
            </Typography.Text>

            <Form.Item name="Description" className="mg-0">
              <Input.TextArea
                data-testid="category-description-input"
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={400}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Description',
                    isEditPage: Boolean(editableCategoryId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <EditPageButtons
            serverError={serverError}
            errorType={ERROR}
            setServerError={setServerError}
            isLoading={isLoading}
            setModalOpen={setModalOpen}
            handleCancel={handleCancel}
            isEditPage={Boolean(editableCategoryId)}
          />
        </Space>
      </Form>

      <ConfirmDeletingElement
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={deleteCategory}
        content="Вы уверены, что хотите удалить категорию?"
        title="Удаление категории"
        modalDataTestId="delete-category-confirmation"
        titleDataTestId="delete-category-confirmation-title"
        descriptionDataTestId="delete-description"
      />
    </div>
  );
};
